import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  PackageForIndividualResponse,
  PackagesClient,
  UserPackageStatus,
} from '../model/fss.api';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class ClientPackageStateService {
  private _client = inject(PackagesClient);
  private _user = inject(UserService);

  loaded = signal(false);
  individualPackage = signal<PackageForIndividualResponse | null>(null);
  availableHours = signal<number>(0);
  scheduledCount = signal<number>(0);

  /** Paid package in use; a pending request (NEW / INVOICE_SENT) doesn't count. */
  hasActivePackage = computed(
    () => this.individualPackage()?.status?.value === UserPackageStatus.ACTIVE,
  );
  canBook = computed(() => this.availableHours() > 0);

  async load(): Promise<void> {
    if (this._user.isIndividual()) {
      await this._loadIndividual();
    } else if (this._user.isCompany()) {
      await this._loadCompany();
    }
    this.loaded.set(true);
  }

  private async _loadIndividual(): Promise<void> {
    const userId = this._user.getUserData.id;
    const res = await firstValueFrom(this._client.getPackagesForIndividual(userId));
    const pkg = res.result;
    if (pkg && pkg.id) {
      // The package may still be a pending request (NEW / INVOICE_SENT); its hours are
      // bookable only once the payment is confirmed.
      const isActive = pkg.status?.value === UserPackageStatus.ACTIVE;
      this.individualPackage.set(pkg);
      this.availableHours.set(isActive ? (pkg.hours ?? 0) : 0);
      this.scheduledCount.set(isActive ? (pkg.activeAppointments ?? 0) : 0);
    } else {
      this.individualPackage.set(null);
      this.availableHours.set(0);
      this.scheduledCount.set(0);
    }
  }

  private async _loadCompany(): Promise<void> {
    const userId = this._user.getUserData.id;
    const res = await firstValueFrom(
      this._client.getCompanyPackageStatsForAdmin(userId),
    );
    const stats = res.result;
    this.availableHours.set(stats?.availableHours ?? 0);
  }
}
